/**
 * Extracts tenant code from the current domain
 *
 * Examples:
 * - thilo.thilo.loc → THILO
 * - biznisoft.thilo.loc → BIZNISOFT
 * - digitalcube.thilo.loc → DIGITALCUBE
 * - localhost → null (unknown tenant)
 */
export function getTenantFromDomain(): string | null {
  const hostname = window.location.hostname;

  // If accessing via localhost, return null (tenant not identifiable)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }

  // Extract subdomain (first part before first dot)
  const parts = hostname.split('.');
  const subdomain = parts[0];

  // Map subdomain to tenant code (uppercase)
  const tenantMap: Record<string, string> = {
    thilo: 'THILO',
    biznisoft: 'BIZNISOFT',
    digitalcube: 'DIGITALCUBE',
    dcube: 'DIGITALCUBE', // Alias
    procoders: 'PROCODERS',
    stratgest: 'STRATGEST',
    rehus: 'REHUS',
    slg: 'SLG',
    mobile: 'MOBILE',
    ardac: 'ARDAC',
  };

  // Return tenant code from map, or uppercase subdomain as fallback
  return tenantMap[subdomain.toLowerCase()] || subdomain.toUpperCase();
}

/**
 * Checks if tenant can be identified from the current domain
 */
export function isTenantIdentifiable(): boolean {
  return getTenantFromDomain() !== null;
}

/**
 * Gets the display name for a tenant code
 */
export function getTenantDisplayName(tenantCode: string | null): string {
  if (!tenantCode) return 'Unknown';

  const tenantNames: Record<string, string> = {
    THILO: 'Thilo',
    BIZNISOFT: 'Biznisoft',
    DIGITALCUBE: 'Digitalcube',
    PROCODERS: 'Procoders',
    STRATGEST: 'Stratgest',
    REHUS: 'Rehus',
    SLG: 'SLG',
    MOBILE: 'Mobile',
    ARDAC: 'Ardac',
  };

  return tenantNames[tenantCode.toUpperCase()] || tenantCode;
}

/**
 * Gets the API base URL for the current environment
 */
export function getApiBaseUrl(): string {
  // Check if we have environment variable override
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) {
    return envUrl;
  }

  // Default to api.thilo.loc:8000 for local development
  const protocol = window.location.protocol;
  const port = ':8000';

  return `${protocol}//api.thilo.loc${port}/v2/api`;
}
